import { Viewer3DContent, VItem, VGroup, VMaterial, VPartPositioning, VCoord, 
         VScale, VGroupRelativePositionSide, VGroupPosition, VDimensionsAttibute,
         VScene, VColor, VGroupSpatialInfo } from '../3d-viewer/viewer-3d-content';
import { conditionApplies } from './conditions';
import { Material, MaterialColor } from './types/catalog-internal';
import { ElementAttachPointPosition, ElementPart, ElementsRoot, InitialCamera,
    JElement, MeshRule, Part } from './types/catalog-types';
import { Composition, ElementInstance, GroupItem, MAIN_SCENE } from './types/composition';
import { OptionValues } from './types/composition';
import { resolveAttachControlsInfo } from './composition-helpers';
import { CatalogRoot } from './types/catalog-internal';
import { calculateCountersUse, calculateInitialsAndDefaults, normalizeCounters, ShownCounterMeshes } from './configurator-helpers';
import { calculateImplicitValues } from './properties';
import { Cart3DIDReference, Cart3DItem, GroupSpatialInfo } from './basket-3d-slice';

const QUALITY_SETTINGS = {
    shadowGeneratorType: 'normal',
    shadowResolution: 256,
    shadowFiltering: 'blur-exp-sm'
}

export const DEFAULT_BACKGROUND_COLOR: VColor = [0.95,0.95,0.95];

export const DEFAULT_CAMERA_TARGET: VCoord = [0,0,0];
export const DEFAULT_CAMERA_POSITION: VCoord = [100,0,0];
export const DEFAULT_BASKET3D_POSITION: VCoord = [0,6,25];

export const DEFAULT_COMPOSE_CAMERA_TARGET: VCoord = [0,0.5,0];
export const DEFAULT_COMPOSE_CAMERA_POSITION: VCoord = [0,1,4];

export const DEFAULT_ADDITIONAL_CAMERA_TARGET: VCoord = [0,0,0];
export const DEFAULT_ADDITIONAL_CAMERA_POSITION: VCoord = [-1,0.25,1];

export const ATTACH_POINT_OFFSET = 0.35;
export const ATTACH_POINT_HEIGHT = 0.02;

export const THUMBNAILS_X_START = 16;
export const THUMBNAILS_X_STEP = 140;
export const THUMBNAILS_Y = 250;
export const THUMBNAILS_SIZE: [ number, number ] = [ 120, 120 ];

export const EXCLUDE_FROM_EXPORT = [ /.*-rotatedelete/, /.*-ap-.*/ ];

const POSITION_FROM_ROTATION: Record<number,Record<ElementAttachPointPosition, VGroupRelativePositionSide>> = {
    0: {
        'left': 'left',
        'right': 'right',
        'front-left': 'front',
        'front-right': 'front'
    },
    90: {
        'left': 'back',
        'right': 'front',
        'front-left': 'left',
        'front-right': 'left'
    },
    180: {
        'left': 'right',
        'right': 'left',
        'front-left': 'back',
        'front-right': 'back'
    },
    270: {
        'left': 'front',
        'right': 'back',
        'front-left': 'right',
        'front-right': 'right'
    }
}

const ALIGN_FROM_ROTATION: Record<number,Record<ElementAttachPointPosition, VGroupRelativePositionSide>> = {
    0: {
        'left': 'back',
        'right': 'back',
        'front-left': 'left',
        'front-right': 'right'
    },
    90: {
        'left': 'right',
        'right': 'right',
        'front-left': 'back',
        'front-right': 'front'
    },
    180: {
        'left': 'front',
        'right': 'front',
        'front-left': 'right',
        'front-right': 'left'
    },
    270: {
        'left': 'left',
        'right': 'left',
        'front-left': 'front',
        'front-right': 'back'
    }
}

const ROTATION_FOR_ATTACH_POSITION: Record<ElementAttachPointPosition, Record<ElementAttachPointPosition, number>> = {
    'left': {
        'left': 180,
        'right': 0,
        'front-left': -90,
        'front-right': -90,
    },
    'right': {
        'left': 0,
        'right': 180,
        'front-left': 90,
        'front-right': 90
    },
    'front-left': {
        'left': 90,
        'right': -90,
        'front-left': 180,
        'front-right': 180
    },
    'front-right': {
        'left': 90,
        'right': -90,
        'front-left': 180,
        'front-right': 180

    }
}

const ATTACH_POINT_ROTATION_FROM_SIDE: Record<VGroupRelativePositionSide, number> = {
    'left': 0,
    'right': 180,
    'front': -90,
    'back': 90,
    'top': 0    // must not happen in attach point
}

export interface CompositionToViewerContent {
    composition: Composition
    thumbnailScenes: CompositionToViewerThumbnailScene[]
    implicitValuesByGroup: Record<string,OptionValues>
    selectedElement?: string
    selectedGroup?: string
}

export interface CompositionToViewerThumbnailScene {
    show: boolean
    scene: string
    camera?: InitialCamera
    position: [ number, number ]
    size: [ number, number ]
    label?: string
}

export interface CompositionToViewerOptions {
    addUIControls: boolean,
    addAttachControls: boolean          // dependent of addUIControls, 
    allowElementSelection: boolean
    allowGroupSelection: boolean
    allowGroupMovement: boolean
    showElementDimensions: boolean
    showGroupDimensions: boolean
    showGlobalDimensions: boolean
    transparentBackground: boolean
}


export function compositionToViewer(
    content: CompositionToViewerContent,
    //view: ContentView,
    options: CompositionToViewerOptions,
    // which counter meshes must be shown
    shownMeshesPerGroup: ShownCounterMeshes,
    // Catalog content (all elements, parts, materials, etc)
    catalogElements: ElementsRoot,
    catalogParts: Part[],
    catalogQualities: Material[],
    catalogColors: MaterialColor[],
    // Base URLS for assets
    modelBase: string,
    materialBase: string,
    // Environment image
    environment?: string
): Viewer3DContent {

    const camera = catalogElements.initial?.camera;
    const scenes: Record<string,VScene> = {
        [MAIN_SCENE]: {
            content: {
                backgroundColor: options.transparentBackground ? undefined : DEFAULT_BACKGROUND_COLOR,
                items: [],
                lights: {
                    hemisphere: true,
                },
                directionalShadow: true,
                shadowCatcher: true,
                globalDimensions: options.showGlobalDimensions,
                environment
            },
            camera: {
                target: camera?.target ?? DEFAULT_CAMERA_TARGET,
                position: camera?.position ?? DEFAULT_CAMERA_POSITION
            }
        }
    }

    for(const thScene of content.thumbnailScenes) {
        scenes[thScene.scene] = {
            content: {
                backgroundColor: options.transparentBackground ? undefined : DEFAULT_BACKGROUND_COLOR,
                items: [],
                lights: {
                    hemisphere: true,
                },
                directionalShadow: true,
                shadowCatcher: false,
                globalDimensions: false,
                environment
            },
            camera: {
                target: DEFAULT_ADDITIONAL_CAMERA_TARGET,
                position: DEFAULT_ADDITIONAL_CAMERA_POSITION
            },
            thumbnailView: {
                show: thScene.show,
                camera: thScene.camera ? {
                                            target: thScene.camera.target ?? DEFAULT_ADDITIONAL_CAMERA_TARGET,
                                            position: thScene.camera.position ?? DEFAULT_ADDITIONAL_CAMERA_POSITION
                                         }
                                         :
                                         {
                                            target: DEFAULT_ADDITIONAL_CAMERA_TARGET,
                                            position: DEFAULT_ADDITIONAL_CAMERA_POSITION                        
                                         },
                position: thScene.position,
                size: thScene.size
            }
        }
    }

    for(let group of filterGroups(content.composition.groups, catalogQualities)) {

        const implicitValues = content.implicitValuesByGroup[group.id];

        const vItemGroup = createViewerGroup(group, shownMeshesPerGroup, implicitValues, 
                                            options, group.id === content.selectedGroup, content.selectedElement,
                                            catalogElements.elements, catalogElements.elementParts, catalogParts, 
                                            catalogQualities, catalogColors,
                                            undefined,
                                            modelBase, materialBase);

        if(!scenes[group.scene]) {
            scenes[group.scene] = {
                content: {
                    backgroundColor: DEFAULT_BACKGROUND_COLOR,
                    items: [],
                    lights: {
                        hemisphere: true,
                    },
                    directionalShadow: true,
                    shadowCatcher: false,
                    globalDimensions: false,
                    environment
                },
                camera: {
                    target: DEFAULT_ADDITIONAL_CAMERA_TARGET,
                    position: DEFAULT_ADDITIONAL_CAMERA_POSITION
                },
                thumbnailView: {
                    show: false,
                    camera: {
                        target: DEFAULT_ADDITIONAL_CAMERA_TARGET,
                        position: DEFAULT_ADDITIONAL_CAMERA_POSITION
                    },
                    position: [ THUMBNAILS_X_START, THUMBNAILS_Y ],
                    size: THUMBNAILS_SIZE
                }
            }
        }

        if(scenes[group.scene]) {
            scenes[group.scene].content.items.push(vItemGroup);
        }

    }

    const viewerContent: Viewer3DContent = {
        qualitySettings: QUALITY_SETTINGS,
        scenes,
        watchedNodes: []
    };

    return viewerContent;

}

export function cartCompositionToViewer(
    cart3d: Cart3DItem[],
    catalogs: Record<string,CatalogRoot>,
    options: CompositionToViewerOptions,
    selectedGroupBasket: number|undefined,
    selectedGroupInstance: number|undefined,
    selectedGroupId: string|undefined,
    layout: GroupSpatialInfo[],
    modelBase: string,
    materialBase: string,
    environment: string    
): [ Viewer3DContent, Record<string, Cart3DIDReference> ] {

    const scene: VScene = {
        content: {
            backgroundColor: DEFAULT_BACKGROUND_COLOR,
            items: [],
            lights: {
                hemisphere: true,
            },
            directionalShadow: true,
            shadowCatcher: true,
            globalDimensions: options.showGlobalDimensions,
            environment
        },
        camera: {
            target: DEFAULT_CAMERA_TARGET,
            position: DEFAULT_BASKET3D_POSITION
        }
    }

    let baseId = 0;
    const idReferences: Record<string, Cart3DIDReference> = {};
    const rootNodes = [];

    for(const cartItem of cart3d) {

        const catalogKey = makeCatalogKey(cartItem.saveState.family, cartItem.saveState.variant)
        const catalog = catalogs[catalogKey];

        const [ initial, defaults ] = calculateInitialsAndDefaults(catalog.properties);
        const groupsForViewer = cloneGroups(filterGroups(cartItem.saveState.groups, catalog.qualities));
        normalizeCounters({ groups: groupsForViewer }, catalog, defaults);

        for(let instance = 0; instance < cartItem.quantity; instance+= 1) {

            let items: VItem[];
            let groupOptions: CompositionToViewerOptions;
            if(catalog.compose) {
                items = scene.content.items;
                groupOptions = options;
            } else {
    
                items = [];
                rootNodes.push(baseId.toString());
                idReferences[baseId.toString()] = {
                    basketId: cartItem.id,
                    instance
                }
                const isSelectedGroup = cartItem.id === selectedGroupBasket &&
                                        instance === selectedGroupInstance;
    
                let position: VGroupSpatialInfo;
                const layoutItem = layout.find(li => li.basketId === cartItem.id &&
                                                     li.instance === instance &&
                                                     li.groupId === undefined);
                if(layoutItem) {
                    position = {
                        position: layoutItem.position,
                        rotation: [ 0, layoutItem.rotation, 0 ]
                    }
                } else {
                    position = {
                        position: [ 0, 0, 0 ],
                        rotation: [ 0, 0, 0 ]
                    }
                }
    
                scene.content.items.push({
                    id: baseId.toString(),
                    position,
                    attributes: {
                        moveable: options.allowGroupMovement,
                        // TODO review
                        showDimensions: isSelectedGroup,
                        clickable: options.allowGroupSelection,
                        useInBoundingBox: true,
                        hideInSnapshot: false,
                        data: {
                            type: 'group',
                            id: baseId.toString()
                        }
                    },
                    items
                });
                groupOptions = {
                    ...options,
                    allowGroupMovement: false,
                    allowGroupSelection: false
                }
    
                if(isSelectedGroup) {
                    scene.content.items.push(makeBasket3DUIControls(baseId.toString(), modelBase))
                }
                baseId += 1;
    
            }
    
            for(const group of groupsForViewer) {
    
                if(group.scene === MAIN_SCENE) {
    
                    const implicitValues = calculateImplicitValues(defaults, group.options, catalog.qualities)
    
                    if(catalog.compose) {
                        rootNodes.push(baseId.toString());
                    }
    
                    const [ rebasedGroup, maxId, groupIdReferences ] = rebaseGroupItemIds(group, baseId);
                    for(const [ newId, oldId ] of Object.entries(groupIdReferences)) {
                        idReferences[newId] = {
                            basketId: cartItem.id,
                            instance,
                            groupId: oldId
                        }
                    }
    
                    const isSelectedGroup = cartItem.id === selectedGroupBasket &&
                                            instance === selectedGroupInstance &&
                                            group.id === selectedGroupId;

                    const  translatedReferences = Object.fromEntries(
                        Object.entries(idReferences)
                            .filter(([newId, ref]) => ref.basketId === cartItem.id &&
                                                      ref.instance === instance &&
                                                      ref.groupId !== undefined)
                            .map(([newId, ref]) => {
                                return [ref.groupId, newId]
                            })
                    );

                    const { shownMeshesPerGroup } = calculateCountersUse(
                        { groups: [rebasedGroup] },
                        catalog,
                        { [rebasedGroup.id]: implicitValues }
                    );
    
                    const vItemGroup = createViewerGroup(rebasedGroup, shownMeshesPerGroup, implicitValues, 
                                                         groupOptions, isSelectedGroup, undefined,
                                                         catalog.elements, catalog.elementParts, catalog.parts, 
                                                         catalog.qualities, catalog.colors,
                                                         translatedReferences,
                                                         modelBase, materialBase);
        
                    const layoutItem = layout.find(li => li.basketId === cartItem.id &&
                                                         li.instance === instance && 
                                                         li.groupId === group.id);
                    
                    if(catalog.compose) {

                        if(layoutItem) {   
                            vItemGroup.position = {
                                position: layoutItem.position,
                                rotation: [ 0, layoutItem.rotation, 0 ]
                            }
                        } else {
                            vItemGroup.position = {
                                position: [ 0, 0, 0 ],
                                rotation: [ 0, 0, 0 ]
                            }
                        }    
                    }
    
                    items.push(vItemGroup);
    
       
                    if(isSelectedGroup) {
                        items.push(makeBasket3DUIControls(baseId.toString(), modelBase))
                    }
    
                    baseId = maxId+1;
    
    
                }
    
            }
    

        }

    }

    const viewerContent: Viewer3DContent = {
        qualitySettings: QUALITY_SETTINGS,
        scenes: {
            [MAIN_SCENE]: scene
        },
        watchedNodes: rootNodes
    };

    return [ viewerContent, idReferences ];

}

export function makeCatalogKey(family: string, variant: string|undefined) {
    return family + ( variant ? ('-' + variant ) : '' );
}


// ==== helpers ====

function filterGroups(groups: GroupItem[], qualities: Material[]) {
    return groups.filter(
        group => !(group.showIf && !conditionApplies(group.showIf, group.options, qualities, []))
    );
}

function cloneGroups(groups: GroupItem[]): GroupItem[] {
    return groups.map(group => ({
        ...group,
        options: { ...group.options },
        counters: group.counters ? { ...group.counters } : undefined,
        elements: group.elements.map(element => ({
            ...element,
            attachedTo: element.attachedTo ? { ...element.attachedTo } : undefined
        }))
    }));
}

/**
 * Reassigns bas
 * @param group 
 * @param baseId 
 * @returns 
 */
function rebaseGroupItemIds(group: GroupItem, baseId: number): [ GroupItem, number, Record<string,string> ] {

    let maxId = baseId+1;
    const resultElements: ElementInstance[] = []
    const idReferences: Record<string,string> = {
        [baseId.toString()]: group.id
    }
    
    for(const element of group.elements) {
        const resultElement: ElementInstance = {
            ...element,
            id: maxId.toString()
        }
        idReferences[maxId.toString()] = element.id;
        maxId += 1;
        resultElements.push(resultElement);
    }

    const result: GroupItem = {
        ...group,
        id: baseId.toString(),
        elements: resultElements
    }

    return [ result, maxId, idReferences ];
}

function createViewerGroup(
    group: GroupItem,
    shownMeshesPerGroup: ShownCounterMeshes,
    implicitValues: OptionValues,

    options: CompositionToViewerOptions,
    selected: boolean,
    selectedElement: string|undefined,

    catalogElements: JElement[],
    catalogElementParts: ElementPart[],
    catalogParts: Part[],
    catalogQualities: Material[],
    catalogColors: MaterialColor[],

    idReferences: Record<string,string>|undefined,

    modelBase: string,
    materialBase: string
) {

    const attachPointControls = resolveAttachControlsInfo([group], catalogElements);
    const showDimensions = selected && options.showGroupDimensions;

    const vItemGroup: VGroup = {
        id: `${group.id}`,
        position: {
            position: group.position,
            rotation: [0,group.rotation,0],
        },
        attributes: {
            moveable: options.allowGroupMovement,
            showDimensions,
            clickable: options.allowGroupSelection,
            useInBoundingBox: true,
            hideInSnapshot: false,
            data: {
                type: 'group',
                id: group.id
            }
        },
        items: []
        
    };

    const elementRotations: Record<string,number> = {};

    for(let element of group.elements) {

        const elementRef = catalogElements.find(e => e.id === element.element);
        if(!elementRef) {
            console.error(`Element ${element.element} not found in catalog`);
            continue;
        }
        const elementParts = catalogElementParts.filter(p => p.element === element.element);
        const appliedParts = elementParts.filter(p => conditionApplies(p.showif, implicitValues, catalogQualities, []));

        const idsByRole: Record<string,string> = {};

        for(const elementPart of appliedParts) {
            if(elementPart.role) {
                idsByRole[elementPart.role] = `${element.id}-${elementPart.part}`;
            }
        }

        let elementRotation = 0;
        let elementPosition: VGroupPosition = [ 0, 0, 0 ];

        if(element.attachedTo) {
            const otherId = idReferences ? idReferences[element.attachedTo.otherId] : element.attachedTo.otherId;
            const refRotation = elementRotations[otherId];
            elementRotation = refRotation + ROTATION_FOR_ATTACH_POSITION[element.attachedTo.otherPosition][element.attachedTo.ownPosition];
            elementRotation = (elementRotation % 360 + 360) % 360;
            const positionSide = POSITION_FROM_ROTATION[refRotation][element.attachedTo.otherPosition];
            const alignSide = ALIGN_FROM_ROTATION[refRotation][element.attachedTo.otherPosition];
            elementPosition = {
                referenceGroup: otherId,
                side: positionSide,
                align: alignSide
            }
        }

        elementRotations[element.id] = elementRotation;

        const showDimensions = element.id === selectedElement && options.showElementDimensions;
        const dimensions: VDimensionsAttibute|undefined = elementRef.dimensions ? [ elementRef.dimensions[0], undefined, elementRef.dimensions[1] ] : undefined;

        const vElementGroup: VGroup = {
            id: `${element.id}`,
            position: {
                position: elementPosition,
                rotation: [0,elementRotation,0]    
            },
            attributes: {
                data: {
                    type: 'element',
                    id: element.id
                },    
                moveable: false,
                showDimensions,
                dimensions,
                useInBoundingBox: true,
                hideInSnapshot: false,
                clickable: options.allowElementSelection
            },
            items: [],
        }

        for(const elementPart of appliedParts) {
            
            const partId = elementPart.part;
            const partRef = catalogParts.find(p => p.id === partId);
            if(!partRef) {
                throw new Error(`Part "${partId}" not found`);
            }

            let partPosition: VPartPositioning | undefined;

            if(elementPart.position) {
                if('refSCoord' in elementPart.position) {
                    const referencePart = idsByRole[elementPart.position.refRole];
                    const delta: VCoord = partRef.position ?? [ 0, 0, 0 ];
                    const referenceMesh = elementPart.position.refSCoord;
                    const mesh = elementPart.position.sCoord;
                    partPosition = {
                        referencePart,
                        referenceMesh,
                        mesh,
                        delta
                    }
                } else if('refRole' in elementPart.position) {
                    const referencePart = idsByRole[elementPart.position.refRole];
                    const delta: VCoord = partRef.position ?? [ 0, 0, 0 ];
                    partPosition = {
                        referencePart,
                        delta
                    }
                }
            } else if(partRef.position) {
                partPosition = partRef.position;
            }

            let rotation = partRef.rotation ?? [0,0,0];
            // From 3DS exports it is rotated
            rotation = [ rotation[0] , rotation[1], rotation[2] ];
            // From 3DS exports it is mirrored by the X axis
            let scale: VScale;
            if(partRef.scale === 'inches') {
                scale = [ 1/2.54, 1/2.54, 1/2.54 ];
            } else if(partRef.scale) {
                scale = [ partRef.scale, partRef.scale, partRef.scale ];
            } else {
                scale = [ 1, 1, 1 ];
            }
            // mirrored if element-part defines it
            if(elementPart.mirrorX) {
                scale[0] = -scale[0];
            }
            if(elementPart.mirrorZ) {
                scale[2] = -scale[2];
            }
            // and mirrored again if part defines it
            if(partRef.mirrorX) {
                scale[0] = -scale[0];
            }
            if(partRef.mirrorZ) {
                scale[2] = -scale[2];
            }

            let animationPercentage = undefined;
            let animationFixed = false;
            if(partRef.animation === false) {
                animationPercentage = 0;
                animationFixed = true;
            } else if(typeof partRef.animation === 'number') {
                animationPercentage = partRef.animation;
                animationFixed = true;
            }

            vElementGroup.items.push({
                id: `${element.id}-${partId}`,
                model: {
                    package: `${modelBase}/${partRef.modelFile.package}`,
                    filename: partRef.modelFile.file,
                },
                position: partPosition ?? [0,0,0],
                rotation, scale,
                animationPercentage,
                animationFixed,
                meshes: (partRef.meshes ?? []).map(mesh => {

                    const material = createViewerMaterial(elementPart, mesh, implicitValues, materialBase, catalogColors);
                    let hide = Boolean(mesh.hide);
                    if(!hide && mesh.counter) {
                        const counterMeshes = shownMeshesPerGroup[mesh.counter];
                        if(!(counterMeshes && counterMeshes.find(cm => cm.elementId === element.id &&
                                                                 cm.partId === partId &&
                                                                 cm.meshName === mesh.test))) {
                            hide = true;
                        }
                    }

                    return {
                        ...mesh,
                        hide,
                        ignoreInLayout: mesh.ignoreInLayout ?? false,
                        textureScale: mesh.textureScale ?? partRef.textureScale ?? 1,
                        isIcon: false,
                        material
                    }
                })
            })

        }

        if(options.addUIControls) {

            if(element.id === selectedElement) {

                vItemGroup.items.push({
                    id: `${element.id}-rotatedelete`,
                    position: {
                        position: {
                            referenceGroup: element.id,
                            side: 'top',
                            offset: 0.5
                        },
                        isBillboard: true,
                        fixedSizeFactor: 0.15
                    },
                    attributes: {
                        moveable: false,
                        showDimensions: false,
                        useInBoundingBox: false,
                        hideInSnapshot: true,
                        clickable: true
                    },
                    items: [
                        {
                            id: `${element.id}-rotatedelete-mesh`,
                            model: {
                                package: `${modelBase}/controls2d`,
                                filename: 'rotate-delete',
                            },
                            position: [0,0,0],
                            rotation: [0,0,0],
                            scale: [0.25,0.25,0.25],
                            animationPercentage: 0,
                            animationFixed: true,
                            meshes: [
                                {
                                    test: "rotate",
                                    hide: false,
                                    textureScale: 1,
                                    isIcon: true,
                                    clickable: true,
                                    data: {
                                        type: 'group-rotate',
                                        group: group.id,
                                        element: element.id
                                    }
                                },
                                {
                                    test: "delete",
                                    hide: false,
                                    textureScale: 1,
                                    isIcon: true,
                                    clickable: true,
                                    data: {
                                        type: 'element-delete',
                                        element: element.id
                                    }
                                }
                            ]
                        }
                    ]

                })

            }

            if(options.addAttachControls) {
                // The attach points are not added as part, but as separate items in the same group 
                // (group level, not element level)

                for(const attachPoint of attachPointControls) {

                    if(attachPoint.elementId === element.id && attachPoint.type !== 'used') {
    
                        const attachPointAlign = ALIGN_FROM_ROTATION[elementRotation][attachPoint.position];
    
                        const attachPointPosition = {
                            referenceGroup: element.id,
                            side: POSITION_FROM_ROTATION[elementRotation][attachPoint.position],
                            align: attachPointAlign,
                            offset: ATTACH_POINT_OFFSET
                        }
    
                        const attachPointRotation = ATTACH_POINT_ROTATION_FROM_SIDE[attachPointPosition.side];
    
                        vItemGroup.items.push({
                            id: `${element.id}-ap-${attachPoint.position}`,
                            position: {
                                position: attachPointPosition,
                                rotation: [0,attachPointRotation,0],
                                
                            },
                            attributes: {
                                data: {
                                    type: 'attach-point',
                                    elementId: element.id,
                                    position: attachPoint.position,
                                    profile: attachPoint.profile
                                },    
                                moveable: false,
                                showDimensions: false,
                                useInBoundingBox: false,
                                hideInSnapshot: true,
                                clickable: true
                            },
                            items: [
                                {
                                    id: `${element.id}-ap-${attachPoint.position}-mesh`,
                                    model: {
                                        package: `${modelBase}/controls2d`,
                                        filename: attachPoint.required ? 'attach-point-required' : 'attach-point-optional',
                                    },
                                    position: [0,ATTACH_POINT_HEIGHT,0],
                                    rotation: [0,0,0],
                                    scale: [0.25,0.25,0.25],
                                    animationPercentage: 0,
                                    animationFixed: true,
                                    meshes: [
                                        {
                                            test: "*",
                                            hide: false,
                                            textureScale: 1,
                                            isIcon: true                                            
                                        }
                                    ]
                                }
                            ]
                        })
    
                    }
                }
    
            }

        }

        vItemGroup.items.push(vElementGroup);
    }

    return vItemGroup;

}

function createViewerMaterial(elementPart: ElementPart, mesh: MeshRule, values: OptionValues, materialBase: string, colors: MaterialColor[]): VMaterial | undefined {

    let material: VMaterial | undefined = undefined;

    if(mesh.material) {

        let materialProperty: string = mesh.material;
        let materialValue: string | null = null;
        if(materialProperty.startsWith('$/')) {
            materialValue = materialProperty.substring(2);
        } else {
            if(elementPart.materials) {
                materialProperty = elementPart.materials[mesh.material];
            }
            materialValue = values[materialProperty];
        }

        if(materialValue) {

            const [ materialCode, colorCode ] = materialValue.split('/');

            let color = colors.find(c => c.code === colorCode && c.material === materialCode);

            if(mesh.useLeatherAccent && color && color.leatherAccent) {  
                color = colors.find(c => c.colorId === color!.leatherAccent);
            }

            if(color) {
                const d = color.definition;
                if(d) {
                    material = {
                        name: color.material + '/' + color.code,
                        alpha: d.alpha,
                        basecolor: d.basecolor,
                        anisotropy: d.anisotropy,
                        clearcoat: d.clearcoat,
                        displacement: d.displacement,
                        ior: d.ior,
                        metalness: d.metalness,
                        metallicReflectanceColor: d.metallicReflectanceColor ?? [1,1,1],
                        roughness: d.roughness,
                        sheen: d.sheen,
                        specular: d.specular
                    }

                    const packageStr = `${materialBase}/${color.qualityPath}/${color.code}`;
                    
                    if(d.baseAndAlpha) {
                        material.baseAndAlpha = {
                            //package: packageStr,
                            filename: packageStr + '/' + 'base-and-alpha'
                        }
                    }
                    if(d.metalnessAndRoughness) {
                        material.metalnessAndRoughness = {
                            //package: packageStr,
                            filename: packageStr + '/' + 'metal-and-rough'
                        }
                    }
                    if(d.normalAndHeight) {
                        material.normalAndHeight = {
                            //package: packageStr,
                            filename: packageStr + '/' + 'normal-and-height'
                        }
                    }
                }
            }


        }

    }

    return material;

}

function makeBasket3DUIControls(baseId: string, modelBase: string): VGroup {
    
    return {
        id: `${baseId}-rotatedelete`,
        position: {
            position: {
                referenceGroup: baseId,
                side: 'top',
                offset: 0.5
            },
            isBillboard: true,
            fixedSizeFactor: 0.15
        },
        attributes: {
            moveable: false,
            showDimensions: false,
            useInBoundingBox: false,
            hideInSnapshot: true,
            clickable: true
        },
        items: [
            {
                id: `${baseId}-rotatedelete-mesh`,
                model: {
                    package: `${modelBase}/controls2d`,
                    filename: 'rotate-delete',
                },
                position: [0.25,0,0],
                rotation: [0,0,0],
                scale: [0.25,0.25,0.25],
                animationPercentage: 0,
                animationFixed: true,
                meshes: [
                    {
                        test: "rotate",
                        hide: false,
                        textureScale: 1,
                        isIcon: true,
                        clickable: true,
                        data: {
                            type: 'group-rotate',
                            group: baseId
                        }
                    },
                    {
                        test: 'delete',
                        hide: true
                    }
                ]
            }
        ]

    }

}


export const EMPTY_VIEWER_CONTENT: Viewer3DContent = {
    qualitySettings: QUALITY_SETTINGS,
    scenes: {
        [MAIN_SCENE]: {
            content: {
                backgroundColor: DEFAULT_BACKGROUND_COLOR,
                items: [],
                lights: {
                    hemisphere: true,
                },
                directionalShadow: true,
                shadowCatcher: true,
                globalDimensions: false,

            },
            camera: {
                target: DEFAULT_CAMERA_TARGET,
                position: DEFAULT_BASKET3D_POSITION
            }
        }
    },
    watchedNodes: []
}